/* @flow */

import type { PointsCollectionRow } from "shop-state/types";

import React, { useContext, useState, useEffect } from "react";
import { useClient, StoreInfoContext } from "entrypoint/shared";
import { pointsPriceByID, usePointsLabel } from "helpers/points";
import { getPointsCollectionList } from "queries";
import useBrowserDimensions from "helpers/use-browser-dimensions";
import { useTranslate } from "@awardit/react-use-translate";
import CoinsBagIcon from "icons/coins-bag.svg";
import ArrowUpIcon from "icons/arrow-up-bonus.svg";
import LeafIcon from "@material-ui/icons/EcoOutlined";
import QuizStarIcon from "@material-ui/icons/StarOutlineRounded";
import UserIcon from "@material-ui/icons/PersonOutlineRounded";
import UserPlusIcon from "@material-ui/icons/PersonAddOutlined";
import CartIcon from "@material-ui/icons/ShoppingCartOutlined";
import CartIconPlus from "@material-ui/icons/AddShoppingCartOutlined";

import styles from "./styles.scss";
import cn from "classnames";

type PointsCollectionList = {
  id: number,
  title: string,
  descr: string,
  rows: Array<PointsCollectionRow>,
};

type RowType = "quiz" | "survey" | "update" | "points" | "text";

const PointsCollection = ({
  className,
  dummyClassName,
}: { className?: string, dummyClassName?: string }): React$Node => {
  const { info: { pointsTypes } } = useContext(StoreInfoContext);
  const { width: browserWidth } = useBrowserDimensions();
  const isDesktop = browserWidth > Number.parseInt(styles.small, 10);
  const pointsType = pointsPriceByID(pointsTypes, "awardit");
  const pointsLabel = usePointsLabel();
  const t = useTranslate();
  const [pointsCollectionData, setPointsCollectionData] =
    useState<PointsCollectionList | null>(null);

  const client = useClient();

  const getPointsCollectionListData = async () => {
    const response = await client(getPointsCollectionList)
      .then(res => res.getPointsCollectionList);

    if (response) {
      setPointsCollectionData(response);
    }
  };

  useEffect(() => {
    getPointsCollectionListData();
  }, []);

  const mapTextToIcon = (iconName: string): React$Node => {
    switch (iconName) {
      case "quiz-star":
      case "quiz_star":
        return <QuizStarIcon className={styles.icon} />;
      case "to-the-top":
      case "to_the_top":
        return <ArrowUpIcon className={styles.icon} />;
      case "cart-plus-solid-full":
        return <CartIconPlus className={styles.icon} />;
      case "coins-solid-full":
        return <CoinsBagIcon className={styles.icon} />;
      case "leaf-solid-full":
        return <LeafIcon className={styles.icon} />;
      case "user-plus-solid-full":
        return <UserPlusIcon className={styles.icon} />;
      case "user-solid-full":
        return <UserIcon className={styles.icon} />;
      default:
        return <CartIcon className={styles.icon} />;
    }
  };

  const renderRowPoints = (row: PointsCollectionRow) => {
    const rowType: RowType = row.type && (
      row.type === "quiz" ||
      row.type === "survey" ||
      row.type === "update" ||
      row.type === "text"
    ) ? row.type : "points";

    const Points = ({ children }: { children: React$Node }): React$Element<"a" | "span"> => {
      if (row.url) {
        return (
          <a className={styles.points} href={row.url}>
            {children}
          </a>
        );
      }

      return (
        <span className={styles.points}>{children}</span>
      );
    };

    switch (rowType) {
      case "quiz":
        const isSingleWord = row.str.trim().split(/\s+/).length === 1;

        return (
          <Points>
            {isSingleWord ?
              t("POINTS_COLLECTION.QUIZ", {
                value: row.val,
                label: pointsLabel().toLowerCase(),
              }) :
              row.str}
          </Points>
        );
      case "survey":
      case "update":
      case "text":
        return <Points>{row.str}</Points>;
      case "points":
      default:
        return pointsType ?
          (<Points>{`${row.val} ${pointsLabel().toLowerCase()}`}</Points>) :
          (<Points>{row.str}</Points>);
    }
  };

  if (!pointsCollectionData) {
    return <PointsCollectionDummy className={dummyClassName} />;
  }

  return (
    <div className={cn(styles.container, "awardit-pointsCollectionBox", className)}>
      <div className={styles.inner}>
        <div className={styles.top}>
          <h2 className={styles.heading}>{pointsCollectionData.title}</h2>
          <p className={styles.description}>{pointsCollectionData.descr}</p>
        </div>
        <div className={styles.content}>
          {isDesktop ?
            <table className={styles.table}>
              <tbody>
                {pointsCollectionData.rows.slice(0, 6).map((row: PointsCollectionRow) => (
                  <tr key={row.nr} className={styles.table__row}>
                    <td className={cn(styles.cell, styles.cell__icon)}>
                      {mapTextToIcon(row.icon)}
                    </td>
                    <td className={cn(styles.cell, styles.cell__desc)}>
                      {row.descr}
                    </td>
                    <td className={cn(styles.cell, styles.cell__points)}>
                      {renderRowPoints(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table> :
            pointsCollectionData.rows.slice(0, 3).map((row: PointsCollectionRow) => (
              <div key={row.nr} className={styles.row}>
                {mapTextToIcon(row.icon)}
                <div className={styles.row__container}>
                  <p className={styles.descr}>{row.descr}</p>
                  {renderRowPoints(row)}
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

const PointsCollectionDummy = ({ className }: { className?: string }): React$Node => {
  return (
    <div className={cn(styles.container, styles.dummy, className)}>
      <div className={styles.inner}>
        <div className={styles.top}>
          <h3 className={styles.heading}>&nbsp;</h3>
          <span className={styles.description}>&nbsp;</span>
          <span className={styles.description}>&nbsp;</span>
        </div>
        <div className={styles.content}>
          <table className={styles.table}>
            <tbody>
              {[...new Array(4)].map((x, i) => (
                <tr key={i} className={styles.table__row}>
                  <td className={cn(styles.cell, styles.cell__icon)}>&nbsp;</td>
                  <td className={cn(styles.cell, styles.cell__desc)}>&nbsp;</td>
                  <td className={cn(styles.cell, styles.cell__points)}>&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PointsCollection;
